import { FC, useMemo, useEffect, useRef } from 'react';
import { Box, Typography, Alert } from '@mui/material';

import { useForm } from '@carcareers/contexts';
import { useRooftopSearchQuery } from '@carcareers/store';
import {
  RooftopPostSelect,
  TextInput,
  LocationInput,
  PhoneInput,
  RooftopOption,
  InfoButton,
  RooftopHelpContent,
  CheckBoxInput,
} from '@carcareers/components';

interface RooftopStepFormProps {
  rooftopName?: string;
}

export const RooftopStepForm: FC<RooftopStepFormProps> = ({ rooftopName = 'rooftop' }) => {
  const { data, setData } = useForm();
  const hasSelectedRooftop = !!data?.rooftop?.id;
  const isConfidential = data?.rooftop?.isConfidential ?? false;
  // Disable checkbox only when a non-confidential rooftop is selected
  const checkboxDisabled = hasSelectedRooftop && !data?.rooftop?.isConfidential;
  // Track the previous confidential state to detect when user toggles TO confidential
  const prevIsConfidentialRef = useRef<boolean | undefined>(undefined);

  // Pre-load rooftops to check if any exist
  const { data: rooftopData } = useRooftopSearchQuery({ q: '_', page: 1, limit: 1 });
  const hasRooftops = (rooftopData?.meta?.totalItems ?? 0) > 0;

  const handleRooftopChange = (rooftop: RooftopOption | null) => {
    setData('rooftop', rooftop?.rooftop);
    setData('job', null);
    // Update the previous confidential ref to prevent clearing fields when selecting a confidential rooftop
    if (rooftop?.rooftop?.isConfidential !== undefined) {
      prevIsConfidentialRef.current = rooftop.rooftop.isConfidential;
    }
  };

  // Handle confidential state changes
  useEffect(() => {
    // Skip on initial mount (prevIsConfidentialRef is undefined)
    if (prevIsConfidentialRef.current === undefined) {
      prevIsConfidentialRef.current = isConfidential;
      return;
    }

    const justBecameConfidential = isConfidential && !prevIsConfidentialRef.current;
    const justBecameNonConfidential = !isConfidential && prevIsConfidentialRef.current;
    prevIsConfidentialRef.current = isConfidential;

    // Clear non-confidential fields when transitioning to confidential (for new rooftops)
    if (justBecameConfidential && !hasSelectedRooftop) {
      setData('rooftop.location', '');
      setData('rooftop.phone', '');
      setData('rooftop.email', '');
      setData('rooftop.website', '');
      setData('rooftop.description', '');
    }

    // Clear the selected rooftop when unchecking confidential while a rooftop is selected
    // (if they can uncheck it with a selected rooftop, it must have been confidential)
    if (justBecameNonConfidential && hasSelectedRooftop) {
      setData('rooftop', null);
      setData('job', null);
    }
  }, [isConfidential, hasSelectedRooftop, setData]);

  // Convert raw rooftop object back to RooftopOption for the autocomplete
  // Only create a RooftopOption if there's an actual selected rooftop (has an id)
  const rooftopValue = useMemo((): RooftopOption | null => {
    if (!data?.rooftop?.id) return null;

    const rooftop = data.rooftop;
    return {
      label: rooftop.name || rooftop.location || `Rooftop #${rooftop.id}`,
      value: rooftop.id,
      rooftop,
    };
  }, [data?.rooftop]);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
        <Typography variant="h2">Set Up Your Rooftop</Typography>
        <InfoButton tooltip="What are rooftops?" title="About Rooftops">
          <RooftopHelpContent />
        </InfoButton>
      </Box>
      <Typography variant="body1">
        Select an existing rooftop or create a new one for this job posting. Need to keep your company details private?
        Choose the confidential option below.
      </Typography>

      {hasRooftops && (
        <>
          <Typography variant="h4">Select an Existing Rooftop</Typography>
          <RooftopPostSelect value={rooftopValue} onChange={handleRooftopChange} />
        </>
      )}

      <CheckBoxInput label="Post Confidentially" name="rooftop.isConfidential" disabled={checkboxDisabled} />

      {isConfidential && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Confidential postings will only show the city and state to job seekers. Your company name and contact details
          will be hidden.
        </Alert>
      )}

      {isConfidential ? (
        <>
          <Typography variant="h4">Choose your Confidential Location</Typography>
          <LocationInput
            required
            fullWidth
            cityStateOnly
            label="City, State (e.g., Austin, TX)"
            name="rooftop.location"
            disabled={hasSelectedRooftop}
          />
        </>
      ) : (
        <>
          {!hasSelectedRooftop && hasRooftops && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h4">Create a New Rooftop</Typography>
            </Box>
          )}
          {!hasSelectedRooftop && !hasRooftops && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h4">Create Your First Rooftop</Typography>
            </Box>
          )}
          <TextInput required fullWidth label="Rooftop Name" name="rooftop.name" disabled={hasSelectedRooftop} />
          <LocationInput required fullWidth label="Location" name="rooftop.location" disabled={hasSelectedRooftop} />
          <PhoneInput required fullWidth label="Phone Number" name="rooftop.phone" disabled={hasSelectedRooftop} />
          <TextInput required fullWidth label="Email" name="rooftop.email" disabled={hasSelectedRooftop} />
          <TextInput fullWidth label="Website" name="rooftop.website" disabled={hasSelectedRooftop} />
          <TextInput
            fullWidth
            multiline
            rows={6}
            label="Business Description"
            name="rooftop.description"
            disabled={hasSelectedRooftop}
          />
        </>
      )}
    </>
  );
};
